/* C1/C2 controls: forward catalogue loop, readable-card sizing and measured chrome.
   Load after hst-c.js. Existing card controls and purchase handlers remain their owners. */
(function () {
  'use strict';
  function frame(callback) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; callback(); });
    };
  }
  function visualCopy(card, kind) {
    var copy = card.cloneNode(true);
    copy.setAttribute('data-carousel-' + kind, '');
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('inert', '');
    copy.removeAttribute('id');
    copy.classList.remove('is-current');
    copy.style.pointerEvents = 'none';
    copy.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
    copy.querySelectorAll('details').forEach(function (el) { el.open = false; });
    copy.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
    return copy;
  }
  function initCarousels() {
    document.querySelectorAll('[data-hst-carousel]').forEach(function (carousel) {
      if (carousel.__hstRevision) return;
      var track = carousel.querySelector('[data-carousel-track]');
      var toggle = carousel.querySelector('[data-carousel-toggle]');
      var cards = track && Array.prototype.slice.call(track.querySelectorAll('.hst-c-door'));
      var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-carousel-dot]'));
      if (!track || !toggle || !cards || cards.length < 2 || dots.length !== cards.length || !carousel.__hstC) return;
      carousel.__hstRevision = true;
      var label = toggle.querySelector('[data-carousel-toggle-label]') || toggle;
      var playLabel = toggle.getAttribute('data-play-label') || label.textContent.trim();
      var pauseLabel = toggle.getAttribute('data-pause-label') || label.textContent.trim();
      var reducedLabel = toggle.getAttribute('data-reduced-label') || playLabel;
      var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
      var status = carousel.querySelector('[data-carousel-status]');
      var manualPause = false, keyboardPause = false, hovering = false, touchCount = 0, pointers = {};
      var lastPointerAt = 0, pressedIntent = null, timer = null, automaticClick = false, wrapping = null;
      var lastWidth = 0, lastHeight = 0;
      function offset(card) {
        return card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - track.clientLeft;
      }
      function visible() {
        var rect = track.getBoundingClientRect();
        var overlap = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth &&
          overlap >= Math.min(rect.height, window.innerHeight) * 0.35;
      }
      function reason() {
        if (!carousel.isConnected) return 'detached';
        if (reduced.matches) return 'reduced-motion';
        if (manualPause) return 'manual';
        if (keyboardPause) return 'keyboard';
        if (hovering) return 'hover';
        if (touchCount || Object.keys(pointers).length) return 'touch';
        if (carousel.querySelector('details[open]')) return 'details';
        if (document.visibilityState === 'hidden') return 'hidden';
        if (!visible()) return 'offscreen';
        return '';
      }
      function enabled() { return !manualPause && !keyboardPause && !reduced.matches; }
      function clearTimer() { if (timer !== null) { window.clearTimeout(timer); timer = null; } }
      function render() {
        var armed = enabled(), blocked = reason();
        toggle.disabled = reduced.matches;
        toggle.setAttribute('aria-pressed', armed ? 'true' : 'false');
        toggle.setAttribute('data-autoplay-state', armed ? 'playing' : 'paused');
        toggle.setAttribute('data-autoplay-blocked', blocked);
        label.textContent = reduced.matches ? reducedLabel : armed ? pauseLabel : playLabel;
        toggle.setAttribute('aria-label', label.textContent);
        carousel.classList.toggle('is-autoplaying', !blocked);
        if (status) status.setAttribute('aria-live', blocked ? 'polite' : 'off');
      }
      function navigate(index, instant) {
        automaticClick = true;
        try {
          /* Set the physical position first: the existing dot handler then sees a zero-distance reset. */
          if (instant) track.scrollTo({ left: offset(cards[index]), behavior: 'instant' });
          dots[index].click();
        } finally { automaticClick = false; }
      }
      function finishWrap(resume) {
        if (!wrapping) return;
        var finished = wrapping;
        wrapping = null;
        if (finished.frame !== null) window.cancelAnimationFrame(finished.frame);
        navigate(0, true);
        finished.copies.forEach(function (copy) { copy.remove(); });
        render();
        if (resume) schedule(Math.max(0, 3000 - (Date.now() - finished.started)));
      }
      function forwardWrap() {
        var step = Math.max(1, offset(cards[1]) - offset(cards[0]));
        /* Fill the track's actual viewport, including a peek, independent of window width. */
        var count = Math.min(cards.length, Math.ceil(track.clientWidth / step) + 1), copies = [];
        for (var i = 0; i < count; i += 1) {
          var copy = visualCopy(cards[i], 'clone');
          track.appendChild(copy); copies.push(copy);
        }
        wrapping = { copies: copies, started: Date.now(), target: offset(copies[0]), frame: null };
        track.scrollTo({ left: wrapping.target, behavior: 'smooth' });
        function settle() {
          if (!wrapping) return;
          if (Math.abs(track.scrollLeft - wrapping.target) < 2 || Date.now() - wrapping.started > 1500) { finishWrap(true); return; }
          wrapping.frame = window.requestAnimationFrame(settle);
        }
        wrapping.frame = window.requestAnimationFrame(settle);
      }
      function advance() {
        timer = null;
        if (reason()) { render(); return; }
        var current = dots.findIndex(function (dot) { return dot.getAttribute('aria-current') === 'true'; });
        if (current < 0) current = 0;
        var next = current + 1, lastOriginalStop = Math.max(0, track.scrollWidth - track.clientWidth);
        /* The last complete viewport already shows every remaining original card.
           Continue left into copies only during the wrap, then reset invisibly; never sweep right. */
        if (next >= cards.length || track.scrollLeft >= lastOriginalStop - 2) forwardWrap();
        else { navigate(next, false); schedule(3000); }
      }
      function schedule(delay) {
        clearTimer(); render();
        if (!reason() && !wrapping) timer = window.setTimeout(advance, delay === undefined ? 3000 : delay);
      }
      function refresh(restart) {
        if (restart) clearTimer();
        if (reason()) {
          clearTimer();
          /* Copies never remain as unclickable cards when a reader takes control. */
          if (wrapping) finishWrap(false);
          render();
        } else if (timer === null && !wrapping) schedule(3000);
        else render();
      }
      function isToggle(target) { return target instanceof Element && !!target.closest('[data-carousel-toggle]'); }
      function startPointer(event) {
        lastPointerAt = Date.now();
        if (event.pointerType !== 'mouse') pointers[event.pointerId] = true;
        if (wrapping) finishWrap(false);
        refresh(true);
      }
      function endPointer(event) {
        if (!Object.prototype.hasOwnProperty.call(pointers, event.pointerId)) return;
        delete pointers[event.pointerId]; refresh(true);
      }
      carousel.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'mouse') { hovering = true; refresh(true); }
      });
      carousel.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'mouse') { hovering = false; refresh(true); }
      });
      carousel.addEventListener('pointerdown', startPointer, { passive: true });
      document.addEventListener('pointerup', endPointer, { passive: true });
      document.addEventListener('pointercancel', endPointer, { passive: true });
      if (!('PointerEvent' in window)) {
        carousel.addEventListener('mouseenter', function () { hovering = true; refresh(true); });
        carousel.addEventListener('mouseleave', function () { hovering = false; refresh(true); });
        carousel.addEventListener('touchstart', function (event) { lastPointerAt = Date.now(); touchCount = event.touches.length; refresh(true); }, { passive: true });
        document.addEventListener('touchend', function (event) { if (touchCount) { touchCount = event.touches.length; refresh(true); } }, { passive: true });
        document.addEventListener('touchcancel', function () { touchCount = 0; refresh(true); }, { passive: true });
      }
      document.addEventListener('keydown', function (event) { if (event.key === 'Tab') lastPointerAt = 0; }, true);
      carousel.addEventListener('focusin', function () {
        if (Date.now() - lastPointerAt > 800) keyboardPause = true;
        refresh(true);
      });
      carousel.addEventListener('keydown', function (event) {
        if (isToggle(event.target) && (event.key === 'Enter' || event.key === ' ')) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].indexOf(event.key) >= 0) { keyboardPause = true; refresh(true); }
      }, true);
      carousel.addEventListener('click', function (event) {
        if (automaticClick || isToggle(event.target)) return;
        if (wrapping) finishWrap(false);
        refresh(true);
      }, true);
      carousel.addEventListener('wheel', function () { refresh(true); }, { passive: true });
      carousel.addEventListener('toggle', function () { refresh(true); }, true);
      toggle.addEventListener('pointerdown', function () { pressedIntent = !enabled(); }, { passive: true });
      toggle.addEventListener('pointerup', function () { window.setTimeout(function () { pressedIntent = null; }, 0); }, { passive: true });
      toggle.addEventListener('pointercancel', function () { pressedIntent = null; }, { passive: true });
      toggle.addEventListener('keydown', function () { pressedIntent = null; });
      toggle.addEventListener('click', function () {
        if (reduced.matches) return;
        var play = pressedIntent === null ? !enabled() : pressedIntent;
        pressedIntent = null; manualPause = !play;
        if (play) keyboardPause = false;
        refresh(true);
      });

      function measureCards() {
        if (!carousel.isConnected || !track.clientWidth) return;
        if (wrapping) finishWrap(false);
        var copies = cards.map(function (card) {
          var copy = visualCopy(card, 'measure');
          copy.style.position = 'absolute'; copy.style.left = '-100000px'; copy.style.top = '0';
          copy.style.width = card.offsetWidth + 'px'; copy.style.flex = 'none';
          copy.style.visibility = 'hidden'; copy.style.height = 'auto';
          copy.style.setProperty('min-height', '0', 'important');
          copy.style.setProperty('max-height', 'none', 'important');
          track.appendChild(copy); return copy;
        });
        var height = Math.max.apply(null, copies.map(function (copy) { return copy.offsetHeight; }));
        copies.forEach(function (copy) { copy.remove(); });
        if (height && height !== lastHeight) {
          track.style.setProperty('--hst-card-height', Math.ceil(height) + 'px');
          cards.forEach(function (card) { card.style.minHeight = 'var(--hst-card-height, 0px)'; });
          lastHeight = height;
        }
        lastWidth = track.clientWidth;
        refresh(false);
      }
      var measureLater = frame(measureCards);
      cards.forEach(function (card) { card.querySelectorAll('img').forEach(function (img) { img.addEventListener('load', measureLater); img.addEventListener('error', measureLater); }); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureLater);
      if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', measureLater);
      if ('ResizeObserver' in window) new ResizeObserver(function () { if (track.clientWidth !== lastWidth) measureLater(); }).observe(track);
      window.addEventListener('resize', measureLater);
      measureLater();

      var refreshVisibility = frame(function () { refresh(false); });
      window.addEventListener('scroll', refreshVisibility, { passive: true });
      document.addEventListener('scroll', refreshVisibility, { passive: true, capture: true });
      window.addEventListener('resize', refreshVisibility);
      if ('IntersectionObserver' in window) new IntersectionObserver(refreshVisibility, { threshold: [0, 0.25, 0.35, 0.5, 0.75, 1] }).observe(track);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') { hovering = false; pointers = {}; touchCount = 0; }
        refresh(true);
      });
      window.addEventListener('pagehide', function () { clearTimer(); if (wrapping) finishWrap(false); });
      window.addEventListener('pageshow', function () { refresh(true); });
      function motionChanged() { refresh(true); }
      if (reduced.addEventListener) reduced.addEventListener('change', motionChanged);
      else if (reduced.addListener) reduced.addListener(motionChanged);
      schedule(3000);
    });
  }
  function initChromeClearance() {
    var bars = Array.prototype.slice.call(document.querySelectorAll('[data-hst-sticky], #ybar'));
    var header = document.querySelector('.htk header');
    var observed = bars.concat(header ? [header] : []);
    if (!observed.length) return;
    var root = document.documentElement, previousBottom = '', previousTop = '';
    function rendered(element) {
      if (!element || !element.isConnected) return false;
      var style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }
    function measure() {
      var clearance = 0;
      bars.forEach(function (bar) {
        /* Existing hst.js/htk.js retain exclusive ownership of the bottom bars. */
        if (!rendered(bar) || !bar.classList.contains('is-on') || bar.getAttribute('aria-hidden') === 'true' || bar.hasAttribute('inert')) return;
        clearance = Math.max(clearance, bar.offsetHeight);
      });
      var bottom = Math.ceil(clearance) + 'px', top = Math.ceil(rendered(header) ? header.offsetHeight : 0) + 'px';
      if (bottom !== previousBottom) { root.style.setProperty('--hst-sticky-clearance', bottom); previousBottom = bottom; }
      if (top !== previousTop) { root.style.setProperty('--hst-header-clearance', top); previousTop = top; }
    }
    var schedule = frame(measure);
    if ('ResizeObserver' in window) {
      var sizes = new ResizeObserver(schedule);
      observed.forEach(function (element) { sizes.observe(element); });
    }
    if ('MutationObserver' in window) {
      var states = new MutationObserver(schedule);
      observed.forEach(function (element) { states.observe(element, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'inert', 'style'] }); });
    }
    window.addEventListener('resize', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    measure();
  }
  function init() { initCarousels(); initChromeClearance(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
